import { Component, ChangeDetectionStrategy, computed, inject, OnInit, OnDestroy, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SidebarService } from '../sidebar.service';
import { ProductType, SheetItem } from '../sidebar.models';
import { SelectionService } from '../../core/services/selection.service';
import { SessionContextService } from '../../core/services/session-context.service';
import { CompareOriginService } from '../../core/services/compare-origin.service';
import { SelectionTrayComponent } from '../selection-tray/selection-tray.component';

interface NavSection {
  id: string;
  label: string;
  route?: string;
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, SelectionTrayComponent],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent implements OnInit, OnDestroy {
  sb = inject(SidebarService);
  private selectionService = inject(SelectionService);
  /** Public so the template can call session.hasModule(...) for per-link gating
   *  inside the RATE SHEETS section. */
  readonly session = inject(SessionContextService);
  readonly router = inject(Router);
  private compareOrigin = inject(CompareOriginService);
  private destroyRef = inject(DestroyRef);
  private messageHandler: any;

  /** Full universe of nav items. Order is preserved; filtered down at
   *  render time by feature flags + workflow flavor. */
  /**
   * Top-of-sidebar nav items. Personal workspace pills (Case Scenarios,
   * Inbox) and the Basket render at the bottom of the sidebar as dark
   * stacked pills outside this list — see sidebar.component.html.
   */
  private readonly allSections: NavSection[] = [
    { id: 'dashboard',     label: 'DASHBOARD',             route: '/dashboard' },
    { id: 'rates',         label: 'RATES',                 route: '/rates' },
    // Both shells need every nav change. Omitting one is what produced the
    // Rate Reports orphan — a page reachable in one shell and invisible in the
    // other. Sits next to RATES because that is where its subject lives.
    { id: 'changes',       label: 'UPCOMING CHANGES',      route: '/changes' },
    // RATE SHEETS is a heading-only section (no route) — clicking just expands it.
    // Houses the 3 broker rate sheet pages + any company spreadsheets the user
    // has been granted access to. Decoupled from RATES so reps without
    // full_rates_grid can still reach their rate sheets.
    // { id: 'rate-sheets',   label: 'RATE SHEETS' , route: '/rate-sheet' },
    { id: 'products',      label: 'PRODUCTS',              route: '/products' },
    // { id: 'benefit', label: 'BENEFIT SEARCH', route: '/benefit-search' }, // Phase 2 rebuild
    { id: 'calculator',    label: 'BENEFIT CALCULATOR',    route: '/calculator' },
    // { id: 'calculator-v2', label: 'BENEFIT CALCULATOR V2', route: '/calculator-v2' },
    // { id: 'calculator-v3', label: 'CALCULATOR V3 · DEV',   route: '/calculator-v3' },
    { id: 'disclosures',   label: 'DISCLOSURE & EXCHANGES', route: '/disclosures' },
    // { id: 'sales',         label: 'MARKET STUDY',          route: '/sales' },
  ];

  /** True when the current URL points at a workspace pill route. Used by
   *  the bottom dark pills to render their active state. */
  isOnRoute(prefix: string): boolean {
    const url = this.router.url.split('?')[0];
    return url === prefix || url.startsWith(prefix + '/');
  }

  goTo(route: string): void {
    this.router.navigateByUrl(route);
  }

  /** Reactive nav list — hides routes the user's permissions / company
   *  subscriptions don't grant. Pre-session it shows everything (so the
   *  dev-user picker pre-flow is unaffected). All filtering is currently
   *  commented out (module permissions earlier, feature flags now), so this
   *  returns the full list post-session too. */
  readonly navSections = computed<NavSection[]>(() => {
    const ctx = this.session.context();
    if (!ctx) return this.allSections;  // pre-session = show all

    // FEATURE FLAGS DISABLED — company-wide kill switches are off in the UI,
    // so calcV2Enabled/disclosureEnabled no longer exist on the session.
    // const calcV2 = this.session.calcV2Enabled();
    // const disclosure = this.session.disclosureEnabled();
    // const flavor = this.session.workflowFlavor();

    let items = this.allSections.filter(s => {
      // // Company-wide kill switches first
      // if (s.id === 'calculator-v2' && !calcV2) return false;
      // if (s.id === 'disclosures' && !disclosure) return false;

      // // Per-user-type module permissions (saved on the admin Permissions tab)
      // if (s.id === 'rates'         && !this.session.hasModule('full_rates_grid')) return false;
      // if (s.id === 'calculator'    && !this.session.hasModule('benefit_calculator')) return false;
      // if (s.id === 'calculator-v2' && !this.session.hasModule('calc_v2')) return false;
      // if (s.id === 'products'      && !this.session.hasAnyModule([
      //   'fa_product_search', 'index_product_search', 'index_var_product_search',
      // ])) return false;
      // // RATE SHEETS section: render if the user has any rate-sheet module OR
      // // any visible company spreadsheet. Decoupled from full_rates_grid so
      // // reps (who only get broker rate sheets) can still reach their sheets.
      // if (s.id === 'rate-sheets') {
      //   const hasRateSheetModule = this.session.hasAnyModule([
      //     'fa_broker_rate_sheet', 'index_broker_rate_sheet', 'index_var_broker_rate_sheet',
      //   ]);
      //   const hasCompanySheet = this.sb.companySheets().length > 0;
      //   if (!hasRateSheetModule && !hasCompanySheet) return false;
      // }
      // // Per-user Market Study suppression (users.ann_mkt_std=False)
      // if (s.id === 'sales'         && !this.session.marketStudyEnabled()) return false;
   
      return true;
    });

    // FEATURE FLAGS DISABLED — the V2-above-legacy reorder depended on the
    // calc_v2 kill switch, so nav order is now static.
    // // Broker flavor with V2 subscribed: surface V2 above legacy benefit calc
    // if (flavor === 'broker' && calcV2) {
    //   const calcIdx = items.findIndex(s => s.id === 'calculator');
    //   const v2Idx = items.findIndex(s => s.id === 'calculator-v2');
    //   if (calcIdx >= 0 && v2Idx > calcIdx) {
    //     const [v2] = items.splice(v2Idx, 1);
    //     items.splice(calcIdx, 0, v2);
    //   }
    // }

    return items;
  });

  openSection = 'rates';
  standardViewsExpanded = true;
  myViewsExpanded = true;
  activeSalesTab = 'dash';

  salesTabs = [
    { key: 'dash', label: 'Dashboard' },
    { key: 'comp', label: 'Company Sales' },
    { key: 'ind', label: 'Industry Analytics' },
    { key: 'prod', label: 'Product Sales' },
    { key: 'vaia', label: 'VA / IA Sales' },
  ];

  typeButtons: { key: ProductType; label: string }[] = [
    { key: 'ia', label: 'IA' },
    { key: 'fa', label: 'FA' },
    { key: 'va', label: 'VA' },
    { key: 'rila', label: 'RILA' },
  ];

  ngOnInit(): void {
    this.messageHandler = (event: MessageEvent) => {
      const data = event.data;
      if (!data?.type) return;
      if (data.type === 'beacon:productSelect' || data.type === 'beacon:basketToggle') {
        const p = data.product;
        if (data.checked) {
          this.selectionService.addProduct({
            id: p.id,
            selectionId: `${p.id}`,
            name: p.name || '',
            carrier: p.carrier || '',
            productType: (p.productType || 'fa') as any,
            contexts: [{
              source: data.type === 'beacon:productSelect' ? 'product-search' : 'benefit-search',
              filters: p.filters || ''
            }]
          });
        } else {
          this.selectionService.deselect(`${p.id}`);
        }
      }
    };
    window.addEventListener('message', this.messageHandler);

    this.syncSectionToRoute(this.router.url);

    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(e => this.syncSectionToRoute(e.urlAfterRedirects));
  }

  ngOnDestroy(): void {
    if (this.messageHandler) {
      window.removeEventListener('message', this.messageHandler);
    }
  }

  private syncSectionToRoute(url: string): void {
    // Comparisons are launched from RATES / UPCOMING CHANGES / PRODUCTS /
    // DISCLOSURE but land on /calculator/compare* or /compare/structural, which
    // would expand BENEFIT CALCULATOR under a user who never went there. Match
    // on the launching page instead, same as the top nav highlight does.
    const effective = CompareOriginService.isCompareSurface(url)
      ? (this.compareOrigin.originUrl() ?? url)
      : url;
    const cleanUrl = effective.split('?')[0];
    // Rate sheets live under their own top-level RATE SHEETS section now.
    if (cleanUrl === '/rate-sheet' || cleanUrl === '/ia-rate-sheet' || cleanUrl === '/rila-rate-sheet') {
      this.openSection = 'rate-sheets';
      return;
    }
    // Broker sub-routes
    // /broker/cases and /broker/cases/:id live under the CASES nav item.
    // (/broker/search was removed for v1.)
    if (cleanUrl.startsWith('/broker/')) {
      this.openSection = 'cases';
      return;
    }
    const match = this.allSections.find(s => s.route === cleanUrl) ||
      this.allSections.find(s => s.route === '/' + (cleanUrl.split('/')[1] || ''));
    if (match) {
      this.openSection = match.id;
    }
  }

  get currentMineSheets(): SheetItem[] {
    return this.sb.mineSheets[this.sb.activeType()];
  }

  private readonly rateSheetRoutes = ['/rate-sheet', '/ia-rate-sheet', '/rila-rate-sheet'];

  selectType(type: ProductType): void {
    this.sb.setActiveType(type);
    const url = this.router.url.split('?')[0];
    if (this.rateSheetRoutes.includes(url)) {
      this.router.navigateByUrl('/rates');
    }
  }

  /** Sections that render an accordion body (children + chevron). Anything not
   *  in this set is rendered as a flat single-click route — no chevron, no
   *  expand/collapse, just navigate. */
  private static readonly SECTIONS_WITH_CHILDREN = new Set([
    'rates', 'rate-sheets', 'products', 'calculator', 'sales',
  ]);

  hasChildren(id: string): boolean {
    return SidebarComponent.SECTIONS_WITH_CHILDREN.has(id);
  }

  /** Two-letter abbreviation rendered in the collapsed rail. Takes the first
   *  letter of the first two words (e.g. "BENEFIT CALCULATOR V2" → "BC",
   *  "RATES" → "RT"). Special-cases the few that collide. */
  abbrFor(id: string, label: string): string {
    const overrides: Record<string, string> = {
      'rates': 'RT',
      'rate-sheets': 'RS',
      'products': 'PR',
      'calculator': 'BC',
      'calculator-v2': 'B2',
      'disclosures': 'DX',
      'sales': 'MS',
      'dashboard': 'DA',
    };
    if (overrides[id]) return overrides[id];
    const words = label.split(/\s+/).filter(Boolean);
    if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
    return label.slice(0, 2).toUpperCase();
  }

  /** Total products in the basket — drives the rail's basket-icon badge. */
  basketCount = computed(() => this.selectionService.productCount());

  /** True when the given route matches the current URL (or is a parent of it).
   *  Drives the 3px navy left-accent on flat-link items. */
  isRouteActive(route?: string): boolean {
    if (!route) return false;
    const url = this.router.url.split('?')[0];
    return url === route || url.startsWith(route + '/');
  }

  toggleSection(id: string): void {
    const section = this.allSections.find(s => s.id === id);
    if (section?.route) {
      this.router.navigateByUrl(section.route);
    }
    // Only toggle accordion state for sections that actually have a body.
    if (this.hasChildren(id)) {
      this.openSection = this.openSection === id ? '' : id;
    }
  }

  selectSheet(id: string, type?: ProductType | 'co'): void {
    this.sb.setActiveSheet(id);
    if (type && type !== 'co') {
      this.sb.setActiveType(type);
    }
    // Navigate to rates page with spreadsheet overlay
    const sheet = this.sb.companySheets().find(s => s.id === id);
    if (sheet?.db && sheet?.ssId && sheet?.companyId) {
      this.router.navigate(['/rates'], {
        queryParams: { sheet: `${sheet.db}:${sheet.ssId}:${sheet.companyId}` },
      });
    }
  }

  /** Click handler for company spreadsheets in the RATE SHEETS section.
   *  Routes to the matching broker rate sheet page with ?ss={db}:{id} so the
   *  page can wire per-sheet filtering later. Falls back to /rates for unknown
   *  db keys (rare — happens only for legacy 'co' sheets). */
  selectCompanySheet(sheet: SheetItem): void {
    this.sb.setActiveSheet(sheet.id);
    if (!sheet.db || !sheet.ssId) return;
    const ROUTE_BY_DB: Record<string, string> = {
      fa: '/rate-sheet',
      ia: '/ia-rate-sheet',
      iva: '/rila-rate-sheet',
    };
    const route = ROUTE_BY_DB[sheet.db];
    if (route) {
      this.router.navigate([route], {
        queryParams: { ss: `${sheet.db}:${sheet.ssId}` },
      });
    } else {
      // Legacy fallback: route to /rates if the user has it.
      this.selectSheet(sheet.id);
    }
  }


  navigateToSalesTab(tab: string): void {
    this.activeSalesTab = tab;
    this.router.navigate(['/sales'], { queryParams: { tab } });
  }
}
